import toast from 'react-hot-toast';
import { Copy, ListTree } from 'lucide-react';
import type { ServerLogEntry } from '@/shared/types/api';

interface LogEntryDetailProps {
  entry: ServerLogEntry;
  onShowTrace: (traceId: string, timestamp?: string) => void;
}

const copy = async (text: string, what: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${what}`);
  } catch {
    toast.error(`Could not copy ${what}`);
  }
};

/**
 * The expanded row. Log text is untrusted (feed titles, exception messages, request paths), so
 * every field renders as a React text node — never as HTML, markdown or a link.
 */
export function LogEntryDetail({ entry, onShowTrace }: LogEntryDetailProps) {
  const properties = Object.entries(entry.properties ?? {});

  return (
    <div className="space-y-3 text-sm">
      <div>
        <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">{entry.message}</pre>
        {entry.messageTruncated && <p className="mt-1 text-xs text-muted-foreground">Message truncated by the server.</p>}
      </div>

      {entry.exception && (
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">Exception</h4>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs text-foreground">
            {entry.exception}
          </pre>
          {entry.exceptionTruncated && <p className="mt-1 text-xs text-muted-foreground">Exception truncated by the server.</p>}
        </div>
      )}

      {(entry.requestPath || entry.sourceContext || entry.traceId || properties.length > 0) && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
          {entry.requestPath && (
            <>
              <dt className="text-muted-foreground">Request path</dt>
              <dd className="break-all font-mono text-foreground">{entry.requestPath}</dd>
            </>
          )}
          {entry.sourceContext && (
            <>
              <dt className="text-muted-foreground">Source</dt>
              <dd className="break-all font-mono text-foreground">{entry.sourceContext}</dd>
            </>
          )}
          {entry.traceId && (
            <>
              <dt className="text-muted-foreground">Trace id</dt>
              <dd className="break-all font-mono text-foreground">{entry.traceId}</dd>
            </>
          )}
          {properties.map(([name, value]) => (
            <div key={name} className="contents">
              <dt className="text-muted-foreground">{name}</dt>
              <dd className="break-all font-mono text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap gap-2">
        {entry.traceId && (
          <>
            <button type="button" className="btn-secondary btn-sm gap-1.5" onClick={() => onShowTrace(entry.traceId!, entry.timestamp)}>
              <ListTree className="h-4 w-4" />
              Show all for this trace
            </button>
            <button type="button" className="btn-ghost btn-sm gap-1.5" onClick={() => copy(entry.traceId!, 'trace id')}>
              <Copy className="h-4 w-4" />
              Copy trace id
            </button>
          </>
        )}
        <button type="button" className="btn-ghost btn-sm gap-1.5" onClick={() => copy(JSON.stringify(entry, null, 2), 'entry')}>
          <Copy className="h-4 w-4" />
          Copy as JSON
        </button>
      </div>
    </div>
  );
}
